import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, User, Eye, FileText, CalendarDays, XCircle, CheckCircle } from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { RELATIONSHIP_LABELS } from '@/features/shared/constants/relationships'
import type { RelationshipType } from '@/features/shared/types/patient.types'
import type { ClinicalAlert } from '../hooks/use-insights'

interface AlertCardProps {
  alert: ClinicalAlert
  onViewDetails?: (id: string) => void
  onCreateEvolution?: (alert: ClinicalAlert) => void
  onScheduleSession?: (alert: ClinicalAlert) => void
  onDismiss?: (alert: ClinicalAlert) => void
  isActing?: boolean
}

const severityColors: Record<string, string> = {
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
}

const severityLabels: Record<string, string> = {
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica',
}

const actionTypeLabels: Record<string, string> = {
  evolution_created: 'Evolução criada',
  session_scheduled: 'Sessão agendada',
  dismissed: 'Dispensado',
}

function getReporterLabel(reporter: { relationship: string; profiles: { full_name: string } } | null): string | null {
  if (!reporter?.profiles?.full_name) return null
  const label = RELATIONSHIP_LABELS[reporter.relationship as RelationshipType] ?? reporter.relationship
  return `${reporter.profiles.full_name} (${label})`
}

export function InsightCard({
  alert,
  onViewDetails,
  onCreateEvolution,
  onScheduleSession,
  onDismiss,
  isActing,
}: AlertCardProps) {
  const isActive = alert.status === 'active'
  const severity = alert.severity as keyof typeof severityColors
  const reporterLabel = alert.source_report?.reporter
    ? getReporterLabel(alert.source_report.reporter)
    : null

  return (
    <Card
      className={`hover:shadow-md transition-shadow ${
        isActive ? 'border-l-4 border-l-orange-400' : 'bg-muted/30'
      }`}
    >
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`p-2 rounded-lg ${isActive ? 'bg-orange-50' : 'bg-muted'}`}>
              <AlertTriangle className={`h-5 w-5 ${isActive ? 'text-orange-600' : 'text-muted-foreground'}`} />
            </div>
            <div className="flex-1 min-w-0">
              {/* Header: severity + category + patient + time */}
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge className={severityColors[severity] ?? 'bg-gray-100 text-gray-800'}>
                  {severityLabels[severity] ?? severity}
                </Badge>
                {alert.category && <Badge variant="outline">{alert.category}</Badge>}
                <Link
                  to="/patients/$patientId"
                  params={{ patientId: alert.patient.id }}
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:underline"
                >
                  <User className="h-3 w-3" />
                  {alert.patient.full_name}
                </Link>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(alert.created_at), {
                    addSuffix: true,
                    locale: ptBR,
                  })}
                </span>
                {!isActive && (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                )}
              </div>

              {/* Reporter info */}
              {reporterLabel && alert.source_report && (
                <p className="text-xs text-muted-foreground mb-1">
                  Relatado por <span className="font-medium text-gray-700">{reporterLabel}</span>
                  {' — '}
                  {format(new Date(alert.source_report.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </p>
              )}

              {/* Alert text */}
              <p className="text-sm">{alert.insight_text}</p>

              {/* Resolution info for resolved alerts */}
              {!isActive && alert.acted_at && (
                <p className="text-xs text-muted-foreground mt-2">
                  {actionTypeLabels[alert.action_type ?? ''] ?? 'Resolvido'}
                  {alert.acted_by_profile && ` por ${alert.acted_by_profile.full_name}`}
                  {' — '}
                  {format(new Date(alert.acted_at), "dd/MM 'às' HH:mm", { locale: ptBR })}
                  {alert.action_note && ` • "${alert.action_note}"`}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 self-end sm:self-auto sm:flex-shrink-0">
            {onViewDetails && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewDetails(alert.id)}
                title="Ver detalhes"
              >
                <Eye className="h-4 w-4" />
              </Button>
            )}
            {isActive && (
              <>
                {onCreateEvolution && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onCreateEvolution(alert)}
                    disabled={isActing}
                    title="Criar evolução"
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                )}
                {onScheduleSession && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onScheduleSession(alert)}
                    disabled={isActing}
                    title="Agendar sessão"
                  >
                    <CalendarDays className="h-4 w-4" />
                  </Button>
                )}
                {onDismiss && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDismiss(alert)}
                    disabled={isActing}
                    title="Dispensar"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Legacy export for backward compatibility
export { InsightCard as AlertCard }
